import { useEffect, useRef, useState } from "react";
import { createRoomInviteBridge } from "./RoomInviteBridge";
import { isRoomInviteRegisteredPlayer, roomInviteGamePath, roomInviteHomePath,
  roomInviteRegistrationPath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

// Access localStorage inside the bridge's guarded operations, not while rendering the app.
const browserStorage = {
  getItem: key => window.localStorage.getItem(key),
  setItem: (key, value) => window.localStorage.setItem(key, value),
};

export function useRoomInvite({ location, history, getPersist, setPersist, getGameUrl, hallOrigin,
  confirmInvite, busy = false, registry = ROOM_INVITE_REGISTRY,
  bridgeWindow = typeof window === "undefined" ? null : window, storage = browserStorage }) {
  const [pending, setPending] = useState(null);
  const bridge = useRef(null), handled = useRef(null), prompted = useRef(""), failedRoute = useRef(null);
  const loaded = getPersist("dataLoaded"), userInfo = getPersist("user_info");
  const current = useRef(null);
  current.current = { loaded, busy, location, history, getPersist, confirmInvite };
  useEffect(() => {
    if (!bridgeWindow) return;
    // Staging needs the static registry and durable storage, not a network-loaded account.
    // Navigation below still waits for the existing identity/persist hydration.
    const receiver = createRoomInviteBridge({ hallOrigin, storage, registry, onPending: setPending });
    bridge.current = receiver;
    handled.current = null;
    failedRoute.current = null;
    prompted.current = "";
    const previous = bridgeWindow.RoomInviteBridge;
    const api = { version: 1, receive: envelope => receiver.receive(envelope), status: id => receiver.status(id) };
    bridgeWindow.RoomInviteBridge = api;
    setPending(receiver.pending());
    return () => {
      if (bridgeWindow.RoomInviteBridge === api) {
        if (previous) bridgeWindow.RoomInviteBridge = previous;
        else delete bridgeWindow.RoomInviteBridge;
      }
      if (bridge.current === receiver) bridge.current = null;
    };
  }, [hallOrigin, storage, registry, bridgeWindow]);

  useEffect(() => {
    if (!loaded || busy) { failedRoute.current = null; return; }
    if (new URLSearchParams(location.search).has("invite_token")) return;
    const receiver = bridge.current;
    // Read the live receiver: an earlier effect may already have accepted a newer delivery.
    const staged = receiver && receiver.pending();
    const home = location.pathname === "/home" || location.pathname === "/";
    const canonicalRoutePath = roomInviteHomePath(location.search, registry);
    const routePath = roomInviteHomePath(location.search, registry, true);
    if (staged?.phase === "routing" && location.pathname === "/game" && staged.homePath === canonicalRoutePath) {
      receiver.finish(staged.deliveryId);
      handled.current = null;
      return;
    }
    if (!home) {
      handled.current = null;
      failedRoute.current = null;
      // Authentication keeps its durable invitation until the matching game route is reached.
      if (!staged || /^\/(login|sendRegCode)$/.test(location.pathname) ||
        prompted.current === staged.deliveryId || typeof confirmInvite !== "function") return;
      prompted.current = staged.deliveryId;
      const id = staged.deliveryId, path = staged.homePath;
      const askedAt = { pathname: location.pathname, search: location.search, key: location.key };
      Promise.resolve(confirmInvite()).then(accepted => {
        const latest = current.current;
        if (!accepted || bridge.current !== receiver || receiver.pending()?.deliveryId !== id ||
            !latest.loaded || latest.busy || latest.location.pathname !== askedAt.pathname ||
            latest.location.search !== askedAt.search || latest.location.key !== askedAt.key) return;
        latest.history.replace(path);
        // "Later" keeps the durable intent for the next visit to the hall.
      }).catch(() => {});
      return;
    }
    const returnPath = staged?.homePath || routePath;
    if (!returnPath) { handled.current = null; failedRoute.current = null; return; }
    const registered = isRoomInviteRegisteredPlayer(userInfo);
    const baseKey = `${registered ? "game" : "register"}:${returnPath}`;
    const key = `${baseKey}:${staged ? staged.deliveryId : ""}`;
    const retryContext = [key, location.pathname, location.search, location.key, userInfo && userInfo.token,
      userInfo && userInfo.rid, userInfo && userInfo.is_guest, userInfo && userInfo.is_demo, getGameUrl, hallOrigin, registry];
    // markRouted publishes state before navigation. Its own update must not retry a failed route in a loop.
    if (failedRoute.current && retryContext.every((value, index) => value === failedRoute.current[index])) return;
    if (handled.current && (handled.current.key === key ||
        (staged?.phase === "routing" && handled.current.baseKey === baseKey))) return;
    const query = new URLSearchParams(returnPath.split("?")[1]);
    try {
      const destination = registered ? roomInviteGamePath(query, getGameUrl, hallOrigin, registry) :
        roomInviteRegistrationPath(returnPath);
      if (!destination || (staged && !receiver.markRouted(staged.deliveryId))) return;
      handled.current = { key, baseKey };
      setPersist("agent_rid", query.get("aid") || "");
      setPersist("agent_from", query.get("f") || "");
      if (!registered) {
        setPersist("isLogin", false);
        setPersist("login_back_url", returnPath);
      }
      if (staged && receiver.pending()?.homePath !== returnPath) { handled.current = null; return; }
      history.replace(destination);
      failedRoute.current = null;
    } catch (_) {
      handled.current = null;
      failedRoute.current = retryContext;
      if (staged) receiver.retryRoute(staged.deliveryId);
    }
  }, [loaded, location.pathname, location.search, location.key, userInfo, getGameUrl, hallOrigin, history, setPersist, getPersist,
    registry, pending, confirmInvite, busy]);
}
